import React, { useState, useEffect } from "react";
import { Box, Typography, CircularProgress, Divider } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../hooks/storeHooks";
import IsMobile from "../../../../hooks/isMobile";
import { fDate } from "../../../../utils/formatTime";
import { isB2CEventActive, isObjectEmpty } from "../../../../helpers/common";
import {
	EVENT_BID_STATUSES,
	iBidType,
	iEventDetailCMS,
	iEventType,
} from "../../../../types/eventTypes";
import { calculateQuoteAmount, getBrandedQuoteHtmlContent } from "../../../../utils/quoteFormUtils";
import { getEventBidThunk } from "../../../../store/events/eventThunk";
import { ROUTE_PATHS } from "../../../../routes";
import QuotesEmptyState from "../quotes-empty-state/QuotesEmptyState";
import PreviewModal from "../../../reusable-components/modals/preview-modal/PreviewModal";
import QuoteCard from "../../quote-card/QuoteCard";
import { iUserDetails } from "../../../../types/userType";
import CoreButtons from "../../../core-components/button/CoreButtons";
import { BUTTON_COLOR, BUTTON_VARIANT } from "../../../../types/coreButtonTypes";
import AdminMessageComponent from "../../admin-message/AdminMessageComponent";
import ViewInvoicesLinks from "../../view-invoices-links/ViewInvoicesLinks";
import "./quotesTabContentB2C.scss";

interface QuotesTabContentProps {
	eventDetail: iEventType;
	cms: iEventDetailCMS;
	isEventDeclined: boolean;
	onClickAddAdditionalQuote?: () => void;
}

const QuotesTabContentB2C: React.FC<QuotesTabContentProps> = ({ eventDetail, onClickAddAdditionalQuote, isEventDeclined = false }) => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const isMobile = IsMobile();
	const { userDetails } = useAppSelector((state) => state.user);
	const {
		bids: { currentBid, history },
		bidsLoading,
		bidsApiError,
	} = useAppSelector((state) => state.events);
	const [showQuoteModal, setShowQuoteModal] = useState(false);
	const [quotePreviewHTML, setQuotePreviewHTML] = useState<string>("");

	const isActiveEvent = isB2CEventActive(eventDetail);
	const quotesCount = history?.length + (!isObjectEmpty(currentBid) ? 1 : 0);
	const adminMessage = eventDetail?.adminMessage ? eventDetail?.adminMessage : "";

	// Fetch bids when component mounts
	useEffect(() => {
		if (eventDetail?.id) {
			dispatch(getEventBidThunk({ id: eventDetail.id }));
		}
	}, [dispatch, eventDetail?.id]);

	const handleEditQuote = async (quote: iBidType) => {
		navigate(`${ROUTE_PATHS.QUOTE_FORM}?eventId=${eventDetail?.id}`, { state: { quote } });
	};

	const handleViewQuote = async (quote: iBidType) => {
		setQuotePreviewHTML(getBrandedQuoteHtmlContent(quote, userDetails as iUserDetails, true));
		setShowQuoteModal(true);
	};

	// Show loading state
	if (bidsLoading) {
		return (
			<Box className="quotesTabContent">
				<Box className="quotesLoadingOrErrorContainer">
					<CircularProgress size={isMobile ? 16 : 24} />
					<Typography sx={{ mt: 2 }}>Loading quotes...</Typography>
				</Box>
			</Box>
		);
	}

	// Show error state
	if (bidsApiError) {
		return (
			<Box className="quotesTabContent">
				<Box className="quotesLoadingOrErrorContainer">
					<Typography>Error loading quotes. Please try again.</Typography>
				</Box>
			</Box>
		);
	}

	//Show empty state
	const renderEmptyState = (message?: string, showAddNewQuoteButton?: boolean) => {
		return (
			<QuotesEmptyState
				className="noQuotesMessage"
				onAddNewQuote={onClickAddAdditionalQuote}
				message={message}
				showAddNewQuoteButton={showAddNewQuoteButton}
			/>
		);
	};

	return (
		<Box className={`quotesTabContentB2C ${adminMessage && isEventDeclined ? "adminMessageQuoteTabContentB2C" : ""}`}>
			<Box className="quotesList">
				{isActiveEvent && <Divider className="quotesTabContent_divider" />}

				{quotesCount > 0 ? (
					<>
						{/* Latest Quote Section */}
						{!isObjectEmpty(currentBid) && (
							<>
								<Typography className="sectionTitle">
									{currentBid?.status === EVENT_BID_STATUSES.Accepted ? "Approved quote" : "Latest quote"}
								</Typography>
								<QuoteCard
									quoteAmount={calculateQuoteAmount(currentBid)}
									quoteDate={fDate(currentBid?.createdAt, "MMM dd, yyyy | hh:mm aaa")}
									notes={currentBid?.comment ?? ""}
									onEditQuote={() => handleEditQuote(currentBid)}
									onViewQuote={() => handleViewQuote(currentBid)}
									isLatest={true}
									isApproved={currentBid?.status === EVENT_BID_STATUSES.Accepted}
									showEditButton={isActiveEvent}
									bidSubmittedByAdmin={currentBid?.bidSubmittedByAdmin}
								/>
							</>
						)}

						{eventDetail?.invoices?.length > 0 && (
							<Box className="invoicesList">
								<ViewInvoicesLinks invoices={eventDetail?.invoices} />
							</Box>
						)}

						{/* Quote History Section */}
						{history?.length > 0 && (
							<Box className="quotesHistoryList">
								<Typography className="sectionTitle">Quote history</Typography>
								{history?.map((quote) => {
									return (
										<QuoteCard
											key={quote.id}
											quoteAmount={calculateQuoteAmount(quote)}
											quoteDate={fDate(quote?.createdAt, "MMM dd, yyyy | hh:mm aaa")}
											onEditQuote={() => handleEditQuote(quote)}
											onViewQuote={() => handleViewQuote(quote)}
											isLatest={false}
											showEditButton={isActiveEvent}
											bidSubmittedByAdmin={quote?.bidSubmittedByAdmin}
										/>
									);
								})}
							</Box>
						)}

						{isActiveEvent && (
							<CoreButtons
								className="sendQuoteButton"
								fullWidth
								buttonText="Send new quote"
								variant={BUTTON_VARIANT.CONTAINED}
								color={BUTTON_COLOR.PRIMARY}
								onclick={onClickAddAdditionalQuote}
							/>
						)}
					</>
				) : isActiveEvent ? (
					renderEmptyState()
				) : (
					renderEmptyState("No quote at the moment.", false)
				)}
			</Box>

			{/* Admin Notes Section */}
			<AdminMessageComponent adminMessage={adminMessage} />

			<PreviewModal
				open={showQuoteModal}
				onClose={() => setShowQuoteModal(false)}
				htmlContent={quotePreviewHTML}
				title=""
			/>
		</Box>
	);
};

export default QuotesTabContentB2C;
